import React, {FC, Fragment} from 'react'
import './styles.css'
import SyntaxHighlighter from 'react-syntax-highlighter'
import {docco, dark, lightfair} from 'react-syntax-highlighter/dist/esm/styles/hljs'
import {useLocation, useParams} from 'react-router-dom'

interface ApiDocsInterface {}

const ApiDocs: FC<ApiDocsInterface> = () => {
  const location = useLocation()

  // Create a URLSearchParams object from the search string
  const API_URL = process.env.REACT_APP_API_URL

  return (
    <>
      <>
        <div
          className='docs__navbar docs__clear'
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <div
            className='docs__row docs__content'
            style={{
              width: '100%',
              maxWidth: '1200px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '15px',
            }}
          >
            <a
              href={'https://klevere.ai'}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img
                className='docs__logo'
                alt='klevere'
                src='https://app.klevere.ai/new-design/image/logo.png'
                style={{
                  maxHeight: '50px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  marginBottom: '10px',
                }}
              />
            </a>
          </div>
        </div>

        <div className='docs__container docs__clear'>
          <div className='docs__row docs__wrapper'>
            <div className='docs__sidepanel'>
              <a className='title' href='#introduction'>
                Introduction
              </a>
              <a className='docs__section' href='#introduction'>
                API Endpoint
              </a>
              <a className='docs__section' href='#introduction'>
                API Key
              </a>
              <a className='docs__section' href='#output_language'>
                Output Language
              </a>
              <a className='docs__section' href='#tone'>
                Tone
              </a>

              <div className='docs__divider docs__left' />
              <a className='title' href='#gettingStarted'>
                Workflows
              </a>
              <a className='docs__section' href='#marketing'>
                Marketing
              </a>
              <a className='docs__section' href='#sales'>
                Sales
              </a>
              <a className='docs__section' href='#hr'>
                Human Resource
              </a>
              <div className='docs__space docs__double' />
            </div>
            <div className='docs__right-col'>
              <h1 id='#'>Introduction</h1>
              <p>
                An AI workforce built for scale, Klevere AI executes multi-step workflows in
                seconds. Utilizing Klevere is equivalent to adding several members to your team.
              </p>

              <h2 id='introduction'> API Key:</h2>
              <p>
                {' '}
                Your API key is located in Profile Settings, under the API tab. Generate your API
                token from there.
              </p>

              <h2 id='apiKey'>
                {' '}
                Base Endpoint: <SyntaxHighlighter>{`${API_URL}/v1`}</SyntaxHighlighter>{' '}
              </h2>

              <h2 id='apiEndpoint'>Output Language:</h2>

              <ol>
                <li>English</li>
                <li>Arabic</li>
                <li>Danish</li>
                <li>Dutch</li>
                <li>French</li>
                <li>German</li>
                <li>Greek</li>
                <li>Hindi</li>
                <li>Indonesian</li>
                <li>Italian</li>
                <li>Malay</li>
                <li>Norwegian</li>
                <li>Portuguese</li>
                <li>English</li>
                <li>Spanish</li>
                <li>Swedish</li>
                <li>Ukrainian</li>
              </ol>

              <h2 id='gettingStarted'>Tone:</h2>
              <ol>
                <li>Conversational</li>
                <li>Enthusiastic</li>
                <li>Humorous</li>
                <li>Professional</li>
              </ol>

              <h2 id='workflows'>Workflows</h2>

              <div className='docs__space single'></div>

              <h3 id='marketing'>
                <b>Marketing</b>
              </h3>

              <div className='docs__space single'></div>
              <h5>1. Image to Product Description.</h5>
              <div className='docs__space single'></div>
              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_visual_product_description`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      url <code>required</code>
                    </td>
                    <td>"https://example.com/y-67-mobile.png"</td>
                    <td>URL to the image or resource.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Hey there, friend! What I've got for you today isn't just a letter, it's the start of something special. This isn't any old 'K'—it's a bold statement. Are you looking for a sleek, modern touch in your docs__space? Something that grabs attention and says, \"I'm all about style\"? Well, you're looking right at it. \n\nThis 'K' is more than just a letter; it's a piece of art. Crafted with crisp lines and balanced design, it stands out with its minimalist yet striking presence. It's the perfect way to personalize your room, office, or even brand your creative docs__space. Imagine this eye-catching monogram greeting guests as they enter—it's sure to spark conversations and ignite curiosity.\n\nThe black and white palette means it'll fit seamlessly no matter your decor, adding that touch of sophistication. And it's not just about looks—this 'K' speaks to those who value design, identity, and expression.\n\nSo, are you ready to make a statement that no one will forget? Add this stunning 'K' to your life and let it be the signature piece that represents the cool, chic, and creative you. Go on, you know you want to!"
}`}
              </SyntaxHighlighter>

              <br />
              <h5>2. Website Headline</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_web_headline`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      product <code>required</code>
                    </td>
                    <td>"Software"</td>
                    <td>The name of the product being described.</td>
                  </tr>
                  <tr>
                    <td>
                      points <code>required</code>
                    </td>
                    <td>"Key features and benefits of the software"</td>
                    <td>Highlights of the product's capabilities.</td>
                  </tr>
                  <tr>
                    <td>
                      audience <code>required</code>
                    </td>
                    <td>"IT experts"</td>
                    <td>The target audience for the content.</td>
                  </tr>
                  <tr>
                    <td>
                      tone <code>required</code>
                    </td>
                    <td>"Conversational"</td>
                    <td>The desired tone of the communication.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "1. Elevate Your IT Game: The Software That Speaks to Experts.\n2. Experts, Meet Your New Secret Weapon in Tech!\n3. IT Gurus: Discover Software That Gets You.\n4. The Conversational Solution to Your Tech Challenges!\n5. Tech Experts: Simplify Your Work with Smart Software.\n6. Crack the Code to IT Success: Expert-Approved Software Awaits!\n7. Navigate Your IT World with Precision: Expert Software Inside.\n8. Converse with Code: Where Software Meets Mastery.\n9. IT Mastery is Just a Click Away - See How!\n10. Join the IT Elite: Experience the Software Evolution!"
}`}
              </SyntaxHighlighter>

              <h5>3. Image to Social Media Caption</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_visual_social_media_caption`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      url <code>required</code>
                    </td>
                    <td>"https://example.com/job-hunting.jpg"</td>
                    <td>URL to the image related to job hunting.</td>
                  </tr>
                  <tr>
                    <td>socialMedia</td>
                    <td>"instagram.com/jobportal"</td>
                    <td>The link to a relevant social media profile, if applicable.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "\"Ever had one of those days where you feel like you've boxed up your motivation with last year's winter clothes? 😅 Hang in there, friends, we all get a little 'wrapped up' sometimes. #JustOneOfThoseDays #UnboxingMotivation\""
}`}
              </SyntaxHighlighter>

              <h5>4. Digital Advertising</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_digital_advertising`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      product <code>required</code>
                    </td>
                    <td>"Software Development"</td>
                    <td>The name of the product or service being offered.</td>
                  </tr>
                  <tr>
                    <td>
                      points <code>required</code>
                    </td>
                    <td>"Key skills and concepts in software development"</td>
                    <td>Highlights of what the product covers.</td>
                  </tr>
                  <tr>
                    <td>
                      audience <code>required</code>
                    </td>
                    <td>"Students"</td>
                    <td>The target audience for the content.</td>
                  </tr>
                  <tr>
                    <td>
                      action <code>required</code>
                    </td>
                    <td>"Learn Programming"</td>
                    <td>The primary action or goal for the audience.</td>
                  </tr>
                  <tr>
                    <td>
                      tone <code>required</code>
                    </td>
                    <td>"Conversational"</td>
                    <td>The desired tone of the communication.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Hey there, student! Ready to dive into the world of software development? Start coding with our intuitive software and unleash your potential. Let's learn programming together!"
}`}
              </SyntaxHighlighter>

              <h5>5. Product Descriptions</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_product_description`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      product <code>required</code>
                    </td>
                    <td>"Provide Medical Service"</td>
                    <td>The name of the service being offered.</td>
                  </tr>
                  <tr>
                    <td>
                      points <code>required</code>
                    </td>
                    <td>"Essential medical products and services offered"</td>
                    <td>Highlights of what the product includes.</td>
                  </tr>
                  <tr>
                    <td>
                      audience <code>required</code>
                    </td>
                    <td>"Students"</td>
                    <td>The target audience for the content.</td>
                  </tr>
                  <tr>
                    <td>
                      tone <code>required</code>
                    </td>
                    <td>"Conversational"</td>
                    <td>The desired tone of the communication.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Hey there, students! Looking for top-notch medical services? Our medical product ensures you get the care you need quickly and conveniently. Say goodbye to long waits and hello to quality healthcare!"
}`}
              </SyntaxHighlighter>

              <h5>6. Feedback to Webpage</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_website_feedback`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      output_language <code>required</code>
                    </td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                  <tr>
                    <td>
                      website <code>required</code>
                    </td>
                    <td>"https://vjudge.net/"</td>
                    <td>URL to the website related to competitive programming.</td>
                  </tr>
                  <tr>
                    <td>
                      goal <code>required</code>
                    </td>
                    <td>"Competitive Programming"</td>
                    <td>The primary objective or focus of the content.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "### Summary of the Website and Services Offered:\n\nVirtual Judge provides a unique platform that aggregates competitive programming problems from various online judges into a single interface. It allows users to practice on problems sourced from over 40 online judges and submit solutions to be judged on those platforms indirectly. This mechanism facilitates holding contests without the need to access test data directly or rely on a single source for competitive programming challenges. The site aims to serve as a comprehensive tool for practicing and honing competitive programming skills by offering a wide variety of problems across different difficulty levels and topics.\n\n### Positive Feedback:\n\n1. Aggregation of Problems: Virtual Judge's strength lies in its aggregation of problems from a vast array of online judges, providing users with unparalleled access to a diversified pool of programming challenges. This variety helps programmers to practice and excel in a broad spectrum of topics.\n2. Convenience: The platform simplifies the process of finding and working on problems by centralizing access. This removes the need for users to have accounts on multiple platforms or to navigate through different interfaces and standards.\n3. Contest Hosting Feature: Allowing users to host their contests using problems from various sources without the need for original test data is a highly beneficial feature. It boosts competitive programming practices and community engagement by offering a seamless contest creation experience.\n\n### Negative Feedback:\n\n1. User Experience (UX): The website's UX might not be as polished or intuitive as some of the individual platforms it aggregates. This can deter new users or frustrate existing ones, particularly those not yet familiar with competitive programming norms.\n2. Branding and Recognition: While aggregating problems, Virtual Judge may miss out on establishing a strong brand identity. It runs the risk of being seen merely as a portal rather than a standalone authority in competitive programming.\n3. Feedback and Learning Resources: The platform might lack direct, interactive feedback mechanisms and learning resources that individual judges offer. This could limit learners' growth by not providing enough context or guidance on improving their skills.\n\n### Improvements:\n\n1. Enhance UX and Interface: Invest in design research to make the website more user-friendly. Implementing a modern, intuitive UI that guides users through problem selection, contest participation, and progress tracking can vastly improve user engagement.\n2. Develop Unique content: Start creating unique educational content, like tutorials, tips for competitive programming, and problem-solving strategies. This not only aids in branding but also adds value for users, making Virtual Judge a go-to resource beyond just problem aggregation.\n3. Interactive Feedback System: Integrate an interactive feedback system where users can receive hints or explanations for challenging problems. Additionally, incorporating a community-driven forum for discussing problems and strategies could foster a more engaging learning environment.\n4. SEO and content Marketing: To address visibility, Virtual Judge should optimize its content for search engines through strategic use of keywords related to competitive programming. Moreover, engaging in content marketing by publishing articles, problem-solving guides, and success stories can boost its online presence.\n5. Partnerships and Collaborations: Forge partnerships with educational institutions and competitive programming communities. These collaborations can aid in brand recognition and establish Virtual Judge as a pivotal component of the competitive programming ecosystem."
}`}
              </SyntaxHighlighter>

              <h5>7. Compare Webpage</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_compare_webpages`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      output_language <code>required</code>
                    </td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                  <tr>
                    <td>
                      webpage_a <code>required</code>
                    </td>
                    <td>"https://vjudge.net/"</td>
                    <td>URL to the website related to competitive programming.</td>
                  </tr>
                  <tr>
                    <td>
                      webpage_b <code>required</code>
                    </td>
                    <td>"https://codeforces.net/"</td>
                    <td>URL to the website related to competitive programming.</td>
                  </tr>
                  <tr>
                    <td>
                      goal <code>required</code>
                    </td>
                    <td>"Competitive Programming"</td>
                    <td>The primary objective or focus of the content.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "### Summary of the Website and Services Offered:\n\nVirtual Judge provides a unique platform that aggregates competitive programming problems from various online judges into a single interface. It allows users to practice on problems sourced from over 40 online judges and submit solutions to be judged on those platforms indirectly. This mechanism facilitates holding contests without the need to access test data directly or rely on a single source for competitive programming challenges. The site aims to serve as a comprehensive tool for practicing and honing competitive programming skills by offering a wide variety of problems across different difficulty levels and topics.\n\n### Positive Feedback:\n\n1. Aggregation of Problems: Virtual Judge's strength lies in its aggregation of problems from a vast array of online judges, providing users with unparalleled access to a diversified pool of programming challenges. This variety helps programmers to practice and excel in a broad spectrum of topics.\n2. Convenience: The platform simplifies the process of finding and working on problems by centralizing access. This removes the need for users to have accounts on multiple platforms or to navigate through different interfaces and standards.\n3. Contest Hosting Feature: Allowing users to host their contests using problems from various sources without the need for original test data is a highly beneficial feature. It boosts competitive programming practices and community engagement by offering a seamless contest creation experience.\n\n### Negative Feedback:\n\n1. User Experience (UX): The website's UX might not be as polished or intuitive as some of the individual platforms it aggregates. This can deter new users or frustrate existing ones, particularly those not yet familiar with competitive programming norms.\n2. Branding and Recognition: While aggregating problems, Virtual Judge may miss out on establishing a strong brand identity. It runs the risk of being seen merely as a portal rather than a standalone authority in competitive programming.\n3. Feedback and Learning Resources: The platform might lack direct, interactive feedback mechanisms and learning resources that individual judges offer. This could limit learners' growth by not providing enough context or guidance on improving their skills.\n\n### Improvements:\n\n1. Enhance UX and Interface: Invest in design research to make the website more user-friendly. Implementing a modern, intuitive UI that guides users through problem selection, contest participation, and progress tracking can vastly improve user engagement.\n2. Develop Unique content: Start creating unique educational content, like tutorials, tips for competitive programming, and problem-solving strategies. This not only aids in branding but also adds value for users, making Virtual Judge a go-to resource beyond just problem aggregation.\n3. Interactive Feedback System: Integrate an interactive feedback system where users can receive hints or explanations for challenging problems. Additionally, incorporating a community-driven forum for discussing problems and strategies could foster a more engaging learning environment.\n4. SEO and content Marketing: To address visibility, Virtual Judge should optimize its content for search engines through strategic use of keywords related to competitive programming. Moreover, engaging in content marketing by publishing articles, problem-solving guides, and success stories can boost its online presence.\n5. Partnerships and Collaborations: Forge partnerships with educational institutions and competitive programming communities. These collaborations can aid in brand recognition and establish Virtual Judge as a pivotal component of the competitive programming ecosystem."
}`}
              </SyntaxHighlighter>

              <h5>8. News Letter</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_newsletter_post`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      topic <code>required</code>
                    </td>
                    <td>"The Impact of Electric Vehicles on Urban Mobility"</td>
                    <td>The main subject of the content.</td>
                  </tr>
                  <tr>
                    <td>
                      keywords <code>required</code>
                    </td>
                    <td>"electric vehicles, urban mobility, sustainability"</td>
                    <td>Comma-separated keywords relevant to the content.</td>
                  </tr>

                  <tr>
                    <td>audience</td>
                    <td>"Urban planners and environmentally conscious consumers"</td>
                    <td>The target audience for the content.</td>
                  </tr>
                  <tr>
                    <td>action</td>
                    <td>"Encourage the adoption of electric vehicles in cities"</td>
                    <td>The action or objective to be conveyed in the content.</td>
                  </tr>
                  <tr>
                    <td>tone</td>
                    <td>"humorous"</td>
                    <td>The tone in which the content should be written.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data": "Hey there, Urban Planners and Green-Thinking Go-Getters!\n\nAs we cruise into a future where sustainability is more than just a buzzword, let’s take a leisurely drive down the impact of electric vehicles on urban mobility. 
  Think of this as our pitstop to recharge ...}`}
              </SyntaxHighlighter>

              <h5>9. Blog Article</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_blog_article`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      title <code>required</code>
                    </td>
                    <td>"top 10 cars in the world"</td>
                    <td>The title of the content.</td>
                  </tr>
                  <tr>
                    <td>
                      keywords <code>required</code>
                    </td>
                    <td>"cars, engine, best"</td>
                    <td>Comma-separated keywords relevant to the content.</td>
                  </tr>
                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                  <tr>
                    <td>tone</td>
                    <td>"informative"</td>
                    <td>The tone in which the content should be written.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data": "Outline Table\n\n| Heading 1          | Introduction: Speeding into the World of Supercars   |\n|--------------------|------------------------------------------------------|\n| Heading 2          | Ferrari LaFerrari: The Definition of Italian Excellence |\n| Heading 3          | Bugatti Chiron: A Symphony of Speed and Luxury       |\n| Heading 4          | McLaren P1: The British Beast                        |\n| Heading 5          | Lamborghini Aventador: The Raging Bull               |\n| Heading 6          | Porsche 911 GT3 RS: Germany's Precision in Motion    |\n| Heading 7          | Koenigsegg Agera RS: The Swedish Sensation           |\n| Heading 8          | Pagani Huayra: Art on Wheels                         |\n| Heading 9          | Aston Martin Valkyrie: The Hypercar Marvel           |\n| Heading 10         | Rimac C_Two: Embracing the Electric Revolution       |\n| Heading 11         | Mercedes-AMG Project One: Formula 1 for the Road     |\n| Heading 12         | Conclusion: The Pinnacle of Automotive Engineering   |\n\nIntroduction: Speeding into the World of Supercars\n\nPicture yourself gripping the leather-clad steering wheel of a gleaming supercar, its engine roaring like a lion ready to pounce. The allure of the world’s top cars isn’t just about speed; it's a fusion of cutting-edge technology, uncompromised luxury, and jaw-dropping design. Delving into the best cars the world has to offer is like embarking on a whirlwind romance, each model boasting its own seductive charm. Fasten your seatbelts; it’s going to be an exhilarating ride through automotive excellence.\n\nFerrari LaFerrari: The Definition of Italian Excellence\n\nWhat comes to mind when you hear the name Ferrari? The LaFerrari epitomizes the apex of Italian engineering and passion. Its 6.3-liter V12 engine paired with an electric motor produces a staggering 950 horsepower, combining raw might with eco-consciousness. This marvel isn't just a car; it’s a testament to Ferrari's unyielding pursuit of perfection.\n\nImagine the LaFerrari's silhouette slicing through the air like a red-hot knife through butter. You can almost hear the symphony of its engine, an orchestration that stirs the soul. The LaFerrari is more than just a vehicle; it’s a statement, a declaration that power and grace can coexist. Its name, translating to \"The Ferrari,\" signifies its status as the benchmark against which all other supercars are measured.\n\nThe interior, a concoction of carbon fiber and Alcantara, is a playground for those who appreciate the finer things in life. Steering wheels brimming with controls and bucket seats that grip you tighter than a lover's embrace make every drive an event to remember. The LaFerrari doesn’t merely transport you from point A to B; it elevates your journey into an unforgettable experience.\n\nIs it worth the price tag? Without a doubt. The LaFerrari isn't just a car; it’s a masterpiece of Italian artistry, engineering, and passion. Owning one signifies not just wealth but a refined taste for life’s best offerings.\n\nBugatti Chiron: A Symphony of Speed and Luxury\n\nIf the Ferrari LaFerrari represents Italian flair, the Bugatti Chiron is the embodiment of French sophistication and brute force. It’s not just fast; with an 8.0-liter W16 engine producing 1,479 horsepower, it's blisteringly, eye-wateringly fast. But there's more to the Chiron than sheer speed. This car is a symphony where each note is tuned to perfection.\n\nClose your eyes and imagine the Bugatti docs__logo, a horseshoe shape that commands reverence and admiration. The Chiron, named after the legendary Monegasque driver Louis Chiron, is not just a piece of machinery but a work of art. Its exterior is an intricate dance of curves and edges, reflecting the light in ways that captivate the beholder.\n\nStep inside, and you’re greeted by an interior that rivals the opulence of a French chateau. The blend of leather, carbon fiber, and polished aluminum is tastefully done, beckoning you to relax and revel in luxury. The seats are plush, offering comfort that’s rare even among supercars, reaffirming that the Chiron was designed not only for speed but for unparalleled comfort.\n\nImagine cruising down the highway in a car that feels as if it could break the sound barrier. Every press of the accelerator unleashes a torrent of power, pushing you back into your seat with a force that’s both exhilarating and intimidating. The Bugatti Chiron is more than just a car; it’s an experience, an event, a moment in time where man and machine become one.\n\nIs the experience worth every penny? For those who can afford it, the Bugatti Chiron is more than a purchase; it’s an investment in a slice of automotive history, a testament to human ingenuity, and the ultimate expression of speed and luxury.\n\nMcLaren P1: The British Beast\n\nHeading north to the isle of Great Britain, the McLaren P1 roars into our list with its quintessential British precision and ferocity. This supercar, born in Woking, showcases the best of British engineering. Powered by a 3.8-liter twin-turbo V8 engine paired with an electric motor, it churns out a combined 903 horsepower. The McLaren P1 isn't just a car; it's a beast that thrives on the thrill of the chase.\n\nImagine this car, looking like a predator poised for the hunt, its form sculpted by wind and engineering genius. The P1’s design, a harmonious blend of art and aerodynamics, is a testament to McLaren's racing pedigree. Its aggressive stance and low-slung body illustrate that this vehicle was meant for the track as much as the road.\n\nSlide into the cockpit, and you’re immersed in an environment that blends minimalism with functionality. Carbon fiber abounds, a reminder that weight is the enemy of speed, and every ounce shed brings the P1 closer to perfection. The seats, firm and supportive, are designed to hold you tight as you navigate sharp turns and blistering straights.\n\nThe sensation of driving the McLaren P1 is akin to piloting a fighter jet. Acceleration is instantaneous, a gut-punch of power that leaves you breathless yet craving more. Each twist and turn of the steering wheel is met with surgical precision, a tribute to McLaren’s Formula 1 expertise.\n\nIs owning a McLaren P1 a dream worth chasing? Absolutely. This car is not just for driving but for experiencing. It embodies the spirit of motorsport and the relentless pursuit of excellence that McLaren has been known for over the decades.\n\nLamborghini Aventador: The Raging Bull\n\nFrom the serene landscapes of England, we storm into the fiery soul of Italy once more with the Lamborghini Aventador. Named after a fearsome bull that fought valiantly in the bullring, the Aventador embodies raw, unbridled power. Its 6.5-liter V12 engine roars with 730 horses, making it as powerful as it is aggressive.\n\nImagine the Aventador's angular frame and sleek lines as it tears through the streets, a beast born of passion and power. It’s a car that doesn't just capture attention; it demands it. The Aventador is a visual symphony, each line and curve carefully crafted to evoke a sense of awe and reverence.\n\nStep inside, and the interior feels like a cockpit of a fighter jet, with a digital display that feeds you vital statistics at a glance. The leather seats, immaculately stitched, offer a throne from which to command your journey. The push-button start, reminiscent of launching a missile, ignites the engine with a fury that echoes the legends of the old bullring.\n\nDriving the Aventador is a visceral experience. The acceleration is ferocious, with a 0-60 time that obliterates any semblance of normalcy. Each gear shift feels like a punch, the power only growing, the engine bellowing louder, making each drive both a pleasure and an adrenaline-soaked adventure.\n\nIs the Aventador worth the heart-pounding excitement? Without question. It’s a car that appeals not just to the senses but to the soul, a machine that turns every journey into a gladiatorial event.\n\nPorsche 911 GT3 RS: Germany's Precision in Motion\n\nGazing towards Germany, we find ourselves marveling at the Porsche 911 GT3 RS, a car that embodies German precision and engineering prowess. With a screaming 4.0-liter flat-six engine producing 502 horsepower, the 911 GT3 RS is a supercar that revels in balance and performance.\n\nPicture the iconic silhouette of the 911, a design perfected over decades, each generation refining what it means to be a Porsche. The GT3 RS takes this a step further with aerodynamic enhancements that make it as visually arresting as it is functionally effective. The massive rear wing isn't just for show; it provides real downforce, planting the car firmly on the ground as you take it to its limits.\n\nInside, the GT3 RS is a marriage between luxury and functionality. Bucket seats grip you tight, a necessary feature when you consider the car's track capabilities. The steering wheel, devoid of unnecessary buttons, is all about offering pure, unfiltered driving feedback. The tachometer, the centerpiece of the dashboard, serves as a constant reminder that the heart of this car is its engine.\n\nDriving the 911 GT3 RS is like wielding a scalpel; precision is its mantra. Every input, from throttle to brake to steering, is met with immediate, unwavering response. It’s a car that rewards skill, requiring the driver to be as sharp and precise as the machine itself.\n\nIs the Porsche 911 GT3 RS a car for everyone? Maybe not. But for those who seek the pinnacle of driver engagement, it's a near-perfect instrument, a harmonious blend of power, precision, and panache.\n\nKoenigsegg Agera RS: The Swedish Sensation\n\nVenturing into the cool, crisp air of Sweden, we encounter the Koenigsegg Agera RS, a car that has carved its name into the annals of automotive legend. Sporting a 5.0-liter twin-turbo V8 engine that generates an eye-popping 1,160 horsepower, the Agera RS is a tour de force of Swedish innovation and craftsmanship.\n\nVisualize the Agera RS slicing through the air, its sleek, low body designed with aerodynamic mastery. This car is a beast that doesn’t just challenge the limits; it redefines them. Each component, from its carbon fiber body to its bespoke engine, is crafted with an obsession for performance and perfection.\n\nInside, the Agera RS is a blend of luxury and futuristic design. The leather and Alcantara materials create a sumptuous environment, while the digital displays and racing-inspired seats remind you that this is a performance machine at heart. The trademark dihedral synchro-helix actuation doors add a touch of theatre to every entrance and exit.\n\nDriving the Agera RS is an experience unlike any other. The power delivery is brutal yet controlled, a tidal wave of force that transforms the landscape into a blur. The handling is razor-sharp, a testament to Koenigsegg’s dedication to not just speed but agility and control. Every corner, every straight, becomes an invitation to push the boundaries.\n\nIs the Agera RS for the faint of heart? Absolutely not. It’s a car that demands respect, both in driving skill and appreciation of its engineering marvel. But for those who dare, it offers an unparalleled journey into the realms of speed and innovation.\n\nPagani Huayra: Art on Wheels\n\nIn our journey through automotive marvels, we find ourselves in the presence of the Pagani Huayra, a car that transcends the realm of transportation into that of art. Named after the Incan god of wind, the Huayra is a divine blend of beauty and performance, powered by a 6.0-liter V12 engine producing 730 horsepower.\n\nImagine the Huayra’s exquisite design, a sculpture in motion, each curve and line a testament to aesthetic and aerodynamic harmony. The car isn’t just built to go fast; it’s designed to be admired, to evoke emotion and desire.\n\nStep inside, and the interior of the Huayra is a feast for the senses. The use of high-quality materials like carbon-fiber, leather, and aluminum creates an environment that feels otherworldly. Every button, every switch, is meticulously crafted, blending old-world craftsmanship with modern technology.\n\nDriving the Huayra is an experience akin to wielding a work of art on the road. The acceleration is smooth yet powerful, the engine’s roar a symphony of mechanical excellence. Each drive is a ballet of control and power, the car responding to every touch with grace and precision.\n\nIs the Huayra worth its place in the pantheon of supercars? Unquestionably. It’s not just a car; it’s a statement of intent, an embodiment of what happens when passion meets engineering brilliance.\n\nAston Martin Valkyrie: The Hypercar Marvel\n\nFlying into British territory once more, we encounter the Aston Martin Valkyrie, a hypercar that is nothing short of a marvel. Collaborating with Red Bull Racing, Aston Martin has created a machine that defies conventional automotive wisdom. Under its hood lies a 6.5-liter V12 engine producing a staggering 1,160 horsepower, a true testament to engineering prowess.\n\nVisualize the Valkyrie as it stands, a car that looks more like a spaceship than a terrestrial vehicle. Its aerodynamic body, crafted from carbon fiber, isn't just for aesthetics; it's a functional necessity designed to keep this beast planted to the ground at speeds that border on surreal.\n\nThe interior of the Valkyrie is a blend of function and futurism. The minimalist design ensures that the driver’s focus remains solely on the experience. Bucket seats, extensive use of carbon fiber, and a steering wheel that resembles a Formula 1 unit all emphasize that this car was built for speed and precision.\n\nDriving the Valkyrie is an experience that pushes the boundaries of what you thought possible. Acceleration is instantaneous, a result of meticulous engineering and lightweight construction. The grip is phenomenal, the car hugging the road with a ferocity that belies its power.\n\nIs the Valkyrie a hypercar built for the masses? Certainly not. But for those who seek the pinnacle of automotive innovation, the Aston Martin Valkyrie stands as an icon, a beacon of what can be achieved when ambition meets excellence.\n\nRimac C_Two: Embracing the Electric Revolution\n\nShifting gears into the future, we find the Rimac CTwo, a car that embraces the electric revolution with unrestrained enthusiasm. Powered entirely by electricity, the CTwo boasts a mind-bending 1,914 horsepower, proving that the future of performance is electrifying.\n\nImagine the sleek, futuristic lines of the C_Two as it silently glides down the road, a stark contrast to the roar of traditional engines. This car isn’t just a vehicle; it’s a glimpse into the future, where performance and sustainability go hand in hand.\n\nInside, the C_Two is a marvel of modern design. The use of high-tech materials and advanced displays create an environment that feels more spaceship than car. The seats are ergonomic, providing both comfort and support, essential for handling the immense power at your disposal.\n\nDriving the Rimac C_Two is an experience unlike any other. The power is instantaneous and relentless, the silence of the electric motors replaced by the rush of air and the whine of machinery. The handling is precise, the grip unyielding, a testament to Rimac’s dedication to pushing the boundaries of what is possible.\n\nIs the C_Two the future of supercars? It certainly makes a compelling case. For those who seek performance without compromise, it offers a thrilling blend of power, precision, and sustainability.\n\nMercedes-AMG Project One: Formula 1 for the Road\n\nOur journey through the world’s top cars concludes with the Mercedes-AMG Project One, a car that brings the thrill of Formula 1 to the road. Powered by a 1.6-liter V6 hybrid engine derived from Mercedes' F1 car, the Project One produces over 1,000 horsepower, making it a hybrid marvel in the truest sense.\n\nVisualize the Project One’s aerodynamic form as it navigates the streets; its design echoes the sleek, purposeful lines of an F1 car. This isn’t just a car; it’s a piece of racing heritage, a direct linkage to the pinnacle of motorsport.\n\nStep inside, and you’re greeted by an interior that is more spaceship than car. The minimalist design, extensive use of carbon fiber, and racing seats all serve to remind you that this is a vehicle built for speed and performance. The controls, minimal and precise, are all designed to keep your focus on the drive.\n\nDriving the Project One is an experience that brings the world of F1 to life. The acceleration is brutal, the handling razor-sharp, each twist and turn an invitation to explore the limits of this engineering marvel. The grip, provided by advanced aerodynamics and engineering, ensures that every ounce of power is translated into forward motion.\n\nIs the Project One a car for everyday driving? No. But for those who crave the thrill of the racetrack in a road-legal package, it offers an unparalleled experience.\n\nConclusion: The Pinnacle of Automotive Engineering\n\nAs we conclude our high-speed journey through the top 10 cars in the world, one thing becomes abundantly docs__clear. These vehicles are not just modes of transportation; they are embodiments of human ingenuity, passion, and the relentless pursuit of perfection. From the raw power of the Ferrari LaFerrari to the electric future of the Rimac C_Two, each car represents a unique blend of performance, luxury, and emotion. They captivate our imaginations, pushing the boundaries of what we thought possible and redefining what it means to drive. So, whether you are an avid car enthusiast or someone who simply appreciates the finer things in life, these supercars offer a glimpse into a world where excellence is the only standard."
}`}
              </SyntaxHighlighter>

              <h5>10. Youtube Video to Blog</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_blog_from_youtube`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      video_url <code>required</code>
                    </td>
                    <td>"https://example.com/video"</td>
                    <td>The URL of the video.</td>
                  </tr>
                  <tr>
                    <td>
                      keywords <code>required</code>
                    </td>
                    <td>"video, tutorial, programming"</td>
                    <td>Comma-separated keywords relevant to the content.</td>
                  </tr>
                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                  <tr>
                    <td>tone</td>
                    <td>"informative"</td>
                    <td>The tone in which the content should be written.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data": "1. The Vast Impact of Linux: How One Operating System Powers Millions

2. Behind the Scenes of Linux Development: A Peek into the World Headquarters

3. The Magic of Open Source: How Collaboration Transforms Technology

4. A Personal Journey: From Solo Developer to Tech Icon

5. The Silent Environment: Why Noise Matters in Software Development

The Vast Impact of Linux: How One Operating System Powers Millions

Imagine a world without Linux. Surprisingly, it’s not a headline you'll come across often, even though this open-source operating system silently powers a significant chunk of our digital lives. For those not in the know, Linux might just be another name in the vast sea of technology buzzwords. But let me take you on a journey to explore how this seemingly obscure operating system has woven itself into the very fabric of computing and the Internet's infrastructure.

Behind the Scenes of Linux Development: A Peek into the World Headquarters ..."

  }`}
              </SyntaxHighlighter>

              <h5>11. Social Media Post</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_social_media_post`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      topic <code>required</code>
                    </td>
                    <td>"Latest Trends in Technology"</td>
                    <td>The main subject.</td>
                  </tr>
                  <tr>
                    <td>
                      goal <code>required</code>
                    </td>
                    <td>"Educate readers about new tech"</td>
                    <td>The primary objective.</td>
                  </tr>
                  <tr>
                    <td>
                      audience <code>required</code>
                    </td>
                    <td>"Tech enthusiasts"</td>
                    <td>The target audience.</td>
                  </tr>
                  <tr>
                    <td>tone</td>
                    <td>"informative"</td>
                    <td>The tone in which the content should be written.</td>
                  </tr>
                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>

                  <tr>
                    <td>socialMedia</td>
                    <td>"X"</td>
                    <td>Social media platforms where the content will be shared.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data": "Discover the Ultimate Driving Machines: Our Top 10 Cars in the World\n\nAre you ready to experience unparalleled power and cutting-edge technology? Dive into our curated list of the top 10 cars in the world, where innovation meets performance.\n\nFrom breathtaking designs to engines that roar with sheer power, these vehicles are not just modes of transport—they are engineering marvels. Whether you are an automotive aficionado or someone who appreciates the finer things in life, our list covers a range of models that define excellence.\n\nFind out which cars have made the cut, featuring state-of-the-art engineering and best-in-class performance. This is your chance to explore the epitome of automotive excellence. Do not miss out on what the world of cars has to offer.\n\nCurious? Click to discover which cars have earned their place as the very best.\n\n#TopCars #AutomotiveExcellence #BestInClass #CarLovers #UltimateDrivingExperience"
}`}
              </SyntaxHighlighter>

              <h5>12. Press release</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_press_release`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      topic <code>required</code>
                    </td>
                    <td>"Neque perspiciatis"</td>
                    <td>The main subject of the content.</td>
                  </tr>
                  <tr>
                    <td>
                      keywords <code>required</code>
                    </td>
                    <td>"Ratione praesentium"</td>
                    <td>Comma-separated keywords relevant to the content.</td>
                  </tr>

                  <tr>
                    <td>audience</td>
                    <td>""</td>
                    <td>The target audience is not specified.</td>
                  </tr>
                  <tr>
                    <td>tone</td>
                    <td>"humorous"</td>
                    <td>The tone in which the content should be written.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data": "FOR IMMEDIATE RELEASE\n\nTop 10 Cars in the World: A Deep Dive into Excellence on Wheels\n\nThe world of automotive engineering never ceases to amaze. With each passing year, 
  car manufacturers across the globe push the boundaries of innovation ... }`}
              </SyntaxHighlighter>

              <div className='docs__space docs__double'></div>

              <h3 id='sales'>
                <b>Sales</b>
              </h3>
              <div className='docs__space single'></div>

              <h5>1. Subject Lines</h5>
              <div className='docs__space single'></div>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_subject_lines`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      product <code>required</code>
                    </td>
                    <td>"Develop Software"</td>
                    <td>The name of the service or project being offered.</td>
                  </tr>
                  <tr>
                    <td>
                      points <code>required</code>
                    </td>
                    <td>"Core software development practices"</td>
                    <td>Highlights of what the product entails.</td>
                  </tr>
                  <tr>
                    <td>
                      audience <code>required</code>
                    </td>
                    <td>"Graduate Student"</td>
                    <td>The target audience for the content.</td>
                  </tr>
                  <tr>
                    <td>
                      tone <code>required</code>
                    </td>
                    <td>"Conversational"</td>
                    <td>The desired tone of the communication.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "1. \"Transform Your Coding Skills\"\n2. \"Upgrade Your Software Game\"\n3. \"Master Software Development\"\n4. \"Boost Your Coding Knowledge\"\n5. \"Elevate Your Programming Skills\"\n6. \"Unleash Your Coding Potential\"\n7. \"Enhance Your Software Abilities\"\n8. \"Revolutionize Your Coding Techniques\"\n9. \"Optimize Your Software Skills\"\n10. \"Empower Your Coding Journey\""
}`}
              </SyntaxHighlighter>

              <h5>2. Sales Proposal</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_sales_proposal`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      points <code>required</code>
                    </td>
                    <td>"Longevity"</td>
                    <td>The key feature or focus of the product.</td>
                  </tr>
                  <tr>
                    <td>
                      goal <code>required</code>
                    </td>
                    <td>"Shoes"</td>
                    <td>The main product being discussed.</td>
                  </tr>
                  <tr>
                    <td>
                      maker <code>required</code>
                    </td>
                    <td>"Parents"</td>
                    <td>The target audience or demographic that would benefit from the product.</td>
                  </tr>
                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Dear Parents,\n\nI understand that finding the perfect pair of shoes for your child can be a challenge. With so many options available, it can be overwhelming to know which one will provide the best fit, comfort, and durability.\n\nAt our store, we have been helping families like yours for many years. Our longevity in the industry has given us the expertise to understand the pain points that parents face when shopping for shoes for their children.\n\nWe know that you want to ensure that your child has a comfortable and supportive pair of shoes that will last through all their daily activities. That's why we offer a wide selection of high-quality footwear that is both stylish and durable.\n\nBy choosing our store, you can trust that you are making the best decision for your child's footwear needs. Our team of experienced professionals is here to help you find the perfect pair of shoes that will meet your child's needs and exceed your expectations.\n\nThank you for considering us for your child's shoe shopping needs. We look forward to helping you find the perfect pair of shoes for your little one.\n\nSincerely,\n[Your Name]"
}`}
              </SyntaxHighlighter>

              <h5>3. Ice Breaker for Cold Outreach</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/ice_breaker`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      linkedin_url <code>required</code>
                    </td>
                    <td>"https://linkedin.com/in/example_user"</td>
                    <td>URL to the candidate's LinkedIn profile.</td>
                  </tr>
                  <tr>
                    <td>
                      first_name <code>required</code>
                    </td>
                    <td>"Example_user"</td>
                    <td>The first name of the individual.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Hi Example_user,\nI noticed on your LinkedIn profile that you recently started a new role as a Software Engineer at Example Ltd. Congratulations on this exciting career move!"
}`}
              </SyntaxHighlighter>

              <h5>4. Personalize LinkedIn Requests</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_linkedin_request`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      linkedin_url <code>required</code>
                    </td>
                    <td>"https://www.linkedin.com/in/dannyholtkamp/"</td>
                    <td>The URL to the LinkedIn profile.</td>
                  </tr>
                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data": "Hi Danny,\n\nI noticed your reflections as a non-tech founder and your journey building two tech companies. Your insights resonated deeply with me. I see great value in connecting.\n\n- [Your Name]"
}`}
              </SyntaxHighlighter>

              <h5>5. Generate Cold Email</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_cold_email`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      company <code>required</code>
                    </td>
                    <td>"Klevere AI"</td>
                    <td>The name of the company.</td>
                  </tr>
                  <tr>
                    <td>
                      send_to <code>required</code>
                    </td>
                    <td>"Marketing Team"</td>
                    <td>The recipient of the communication.</td>
                  </tr>
                  <tr>
                    <td>company_summary</td>
                    <td>"A leading provider of innovative tech solutions."</td>
                    <td>A brief summary of the company.</td>
                  </tr>
                  <tr>
                    <td>product_background</td>
                    <td>"Developed cutting-edge software for project management."</td>
                    <td>Information about the product.</td>
                  </tr>
                  <tr>
                    <td>your_company</td>
                    <td>"Example Solutions Inc."</td>
                    <td>The name of your company.</td>
                  </tr>
                  <tr>
                    <td>person_context</td>
                    <td>""</td>
                    <td>Information about the person involved, if applicable.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data": "Subject: Enhancing Klevere AI's Growth with Strategic Solutions\n\nHi [Marketing Team],\n\nI recently admired Klevere AI's campaign for \"Innovative Tech Week\" and how it spotlighted your creative strengths in a fresh, impactful way.\n\nWe at Example Solutions Inc. have designed cutting-edge software that could uniquely bolster Klevere AI's offerings:\n\n- Exclusive Integration with Advanced Analytics: Boost your project management software with real-time insights, predictive analytics, and custom reporting. Enabling smarter decisions for your diverse clientele.\n\n- Bespoke Project Management Automation: Tailor your automation capabilities to reduce manual tasks, appeal to high-efficiency seekers, and justify a higher subscription tier.\n\n- Strategic Partnership and Referral Program: Forge a referral network, enabling cross-selling opportunities and expanding your market reach.\n\nWould you have time for a quick chat this/next week? This is my calendly link: [insert_calendar].\n\nBest regards,\n[Your Name]\nExample Solutions Inc."
}`}
              </SyntaxHighlighter>

              <h5>6. Pain-point Cold Email</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_pain_point_cold_email`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>

              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      first_name <code>required</code>
                    </td>
                    <td>"John"</td>
                    <td>The first name of the contact person.</td>
                  </tr>
                  <tr>
                    <td>
                      company_name <code>required</code>
                    </td>
                    <td>"Klevere AI"</td>
                    <td>The name of the company.</td>
                  </tr>
                  <tr>
                    <td>pain_point</td>
                    <td>"Struggling with inefficient data analysis."</td>
                    <td>The primary challenge faced by the company.</td>
                  </tr>
                  <tr>
                    <td>solution</td>
                    <td>"Implementing AI-driven analytics tools."</td>
                    <td>The proposed solution to the pain point.</td>
                  </tr>
                  <tr>
                    <td>linkedin_url</td>
                    <td>"https://www.linkedin.com/in/dannyholtkamp/"</td>
                    <td>The URL to the LinkedIn profile of the contact person.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`
          {
  "success": true,
  "data": "Hi John,\n\nI saw on your LinkedIn profile your insights on AI-driven personalization in marketing - it caught my attention, and I'm impressed.\n\nI understand that companies in your industry often grapple with inefficient data analysis. This can lead to missed opportunities and suboptimal strategies. Are these challenges something you currently face at Klevere AI?\n\nAt our company, we specialize in implementing AI-driven analytics tools designed to streamline and enhance data analysis. Our advanced solutions not only automate the analytical processes but also provide actionable insights, empowering your team to make informed decisions swiftly.\n\nWould you have time for a quick chat this/next week? This is my Calendly link: [insert_calendar].\n\nBest regards,\n[Your Name]"
}
          `}
              </SyntaxHighlighter>

              <h5>7. Company Research with Website</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_research_with_website`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      company_url <code>required</code>
                    </td>
                    <td>"https://www.klevere.ai/"</td>
                    <td>The URL to the company website.</td>
                  </tr>
                  <tr>
                    <td>
                      product_context <code>required</code>
                    </td>
                    <td>"social media manager"</td>
                    <td>The context or role of the product.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data": "1. Optimizing Social Media Strategy: Klevere’s AI-driven workflows enable efficient content creation for social media, transforming images into compelling captions and repurposing blog posts. Your Social Media Manager service can enhance this capability by providing strategic oversight and ensuring consistent, brand-aligned messaging.\n\n2. Enhancing Engagement: Klevere helps in generating impactful blogs and visuals customized for different audience tones. Your expertise can boost engagement by guiding the creation of relatable, audience-specific content that resonates, improving social channel performance.\n\n3. Streamlining Outreach: With tools for personalizing LinkedIn requests and cold emails, Klevere aids efficient lead conversion. Your service can refine these efforts, ensuring outreach is personalized and targeted, increasing acceptance rates and prospects' responsiveness.\n\n4. Accelerating Workflow: Klevere promises 100X faster execution of tasks, a significant advantage in any marketing campaign. Your management can optimize these automated processes, ensuring the quality and strategic alignment of the outputs."
} `}
              </SyntaxHighlighter>
              <h5>8. Company Research with LinkedIn</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_company_linkedin_research`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      company_linkedin_url <code>required</code>
                    </td>
                    <td>"https://www.linkedin.com/company/nike"</td>
                    <td>The URL to the company's LinkedIn profile.</td>
                  </tr>
                  <tr>
                    <td>
                      product_context <code>required</code>
                    </td>
                    <td>"Digital Advertising Agency"</td>
                    <td>The context or role of the product.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "research":  "1. Purpose-Driven Organization: Example's mission to inspire and innovate aligns perfectly with your digital advertising services, which could amplify their message and reach a broader, diverse audience effectively.\n\n2. Global Presence: With locations across the US and Europe, Example requires consistent, impactful advertising campaigns. Your agency can provide tailored digital marketing solutions to ensure brand cohesion and optimal market penetration.\n\n3. Large Workforce and Diverse Talent Pool: With over 76,000 employees, internal engagement campaigns through digital platforms can enhance morale and productivity. Your services can drive tailored communication strategies.\n\n4. Recent Funding Round: Example’s recent funding indicates potential expansion plans. Your agency can support these initiatives through targeted digital campaigns, maximizing ROI and ensuring strategic growth in new markets."
}`}
              </SyntaxHighlighter>
              <h5>9. Industry Research</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_industry_research`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      industry <code>required</code>
                    </td>
                    <td>"Tech"</td>
                    <td>The industry in which the company operates.</td>
                  </tr>
                  <tr>
                    <td>
                      product_context <code>required</code>
                    </td>
                    <td>"Digital Advertising Agency"</td>
                    <td>The context or role of the product.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data": "In the ever-evolving landscape of digital advertising, staying updated on the latest tech trends is crucial for maintaining competitiveness and seizing new opportunities. Recent analyses from Forbes, McKinsey, Simplilearn, and Globant highlight several key trends that will shape 2024. Here’s a breakdown of these trends and how our Digital Advertising Agency can help businesses navigate the associated challenges and opportunities:\n\n1. Artificial Intelligence (AI) Integration\n   - Challenge: The rapid adoption of AI technology necessitates businesses to integrate AI into their operations effectively. Failure to do so can result in falling behind the competition.\n   - Opportunity: Our agency specializes in leveraging AI-driven analytics to craft personalized advertising campaigns, enhancing customer engagement and targeting precision. We help businesses harness AI for real-time data analysis and predictive modeling, ensuring that their marketing strategies are always ahead of the curve.\n\n2. Augmented Reality (AR) and Virtual Reality (VR)\n   - Challenge: Technologies like AR and VR require substantial investment and technical expertise to implement effectively.\n   - Opportunity: We provide end-to-end digital solutions that incorporate AR and VR into advertising strategies, creating immersive experiences that captivate audiences. Our team can develop VR ad demos and AR product try-ons, offering innovative ways to engage with potential customers.\n\n3. Quantum Computing\n   - Challenge: The complexity and advanced nature of quantum computing make it difficult for businesses to understand and utilize its potential benefits.\n   - Opportunity: By integrating quantum computing into our digital advertising services, we enhance data processing capabilities, leading to unprecedented levels of optimization and efficiency in ad targeting. We offer consulting to help clients grasp and apply quantum technologies to their marketing efforts.\n\n4. 5G Expansion\n   - Challenge: The expansion of 5G networks means businesses need to optimize their content for faster, more reliable internet connections.\n   - Opportunity: Our agency aids businesses in creating high-quality, data-rich advertisements that leverage 5G’s faster speeds. We optimize ads for mobile devices to take full advantage of 5G, ensuring seamless viewing experiences that boost engagement rates.\n\n5. AI-Generated content\n   - Challenge: Businesses must balance automated content production with maintaining a unique brand voice.\n   - Opportunity: Our agency offers AI-driven content creation tools while also providing human oversight to ensure that the brand’s authenticity is preserved. This hybrid approach allows for efficient content generation without sacrificing quality or brand identity.\n\nIn summary, these tech trends present both challenges and significant opportunities for businesses in 2024. Our Digital Advertising Agency is well-positioned to steer clients through these developments, offering cutting-edge solutions that enhance marketing effectiveness and propel business growth."
}`}
              </SyntaxHighlighter>
              <h5>10. Resource Finder</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_linkedin_resource_finder`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      linkedin_url <code>required</code>
                    </td>
                    <td>"https://nl.linkedin.com/in/dannyholtkamp"</td>
                    <td>The LinkedIn URL of the user.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data":  "Hi Danny, I was studying your profile and would like to suggest the newsletter \"Inside AI\" by Rob May, given your role in developing AI tools at Klevere AI. This newsletter covers trends and innovations in AI, which could provide valuable insights to support your current projects.\n\nHave you read this previously?
}
`}
              </SyntaxHighlighter>
              <h5>11. 3 Key Benefits</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_subject_lines`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      linkedin_url<code>required</code>
                    </td>
                    <td>"https://nl.linkedin.com/in/dannyholtkamp"</td>
                    <td>The LinkedIn URL of the user.</td>
                  </tr>
                  <tr>
                    <td>
                      first_name<code>required</code>
                    </td>
                    <td>"Danny"</td>
                    <td>The first name of the user.</td>
                  </tr>
                  <tr>
                    <td>
                      my_company<code>required</code>
                    </td>
                    <td>"Digital Advertising Agency"</td>
                    <td>The name of the company the user is associated with.</td>
                  </tr>
                  <tr>
                    <td>
                      product_context <code>required</code>{' '}
                    </td>
                    <td>"Digital Advertising Agency"</td>
                    <td>The context or role of the product the company offers.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the content should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
  "success": true,
  "data":  "1. Leveraging our AI-driven advertising solutions, you can boost your team's performance at Klevere AI, aligning with your goal to execute tasks 100X faster.\n\n2. Our targeted campaigns can support Leadriver's commitment to accelerating client acquisition with tailored strategies for your audience.\n\n3. By integrating advanced tech with marketing, you'll enhance your experience at ML6 AI, maximizing ROI at both companies."
}`}
              </SyntaxHighlighter>

              <div className='docs__space docs__double'></div>
              <h4 id='hr'>
                <b>Human Resource</b>
              </h4>
              <div className='docs__space single'></div>

              <h5>1. Performance Evaluation</h5>

              <br />

              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {'POST ${API_URL}/v1/workflow/generate_performance_evaluation'}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      contributions <code>required</code>
                    </td>
                    <td>"Developed a new feature for the app"</td>
                    <td>The contributions made by the employee.</td>
                  </tr>
                  <tr>
                    <td>
                      improvement <code>required</code>
                    </td>
                    <td>"Improve code efficiency"</td>
                    <td>The area where the employee can improve.</td>
                  </tr>
                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which to receive the output.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Performance Evaluation\n\nEmployee Name: [Employee's Name]\nPosition: Software Developer\n\nKey Contributions:\n[Employee's Name] has consistently demonstrated exceptional technical proficiency in software development throughout the evaluation period. Their key contributions include:\n- Successfully leading the development of a critical software application that significantly improved efficiency within the team.\n- Demonstrating outstanding problem-solving skills in resolving complex technical issues, helping to streamline processes and enhance overall productivity.\n- Collaborating effectively with team members on various projects, showcasing strong communication and teamwork skills.\n- Taking the initiative to mentor junior developers, contributing to the professional growth and development of the team.\n\nArea of Improvement: Software\nWhile [Employee's Name] has excelled in their role as a Software Developer, there are opportunities for further growth and development in the following areas:\n1. Advanced Coding Skills: [Employee's Name] has shown proficiency in coding, but there is room for improvement in mastering advanced coding techniques. Enhancing expertise in coding languages such as Python, Java, or C++ would further elevate their capabilities in software development.\n2. Innovation and Creativity: Encouraging [Employee's Name] to think outside the box and explore innovative solutions to complex problems can help foster a more creative approach to software development. Encouraging experimentation and exploring new technologies could lead to breakthrough solutions.\n3. Time Management: While [Employee's Name] has met project deadlines effectively, improving time management skills can help them work more efficiently and prioritize tasks more effectively. Implementing time-tracking tools and setting docs__clear milestones can aid in better time management practices.\n\nOverall, [Employee's Name] has shown exceptional dedication and commitment in their role as a Software Developer. With a focus on continuous learning and improvement in the identified areas, [Employee's Name] has the potential to further excel in their career and make even greater contributions to the team.\n\nI look forward to seeing [Employee's Name] grow and develop in the coming evaluation period.\n\nSincerely,\n[Your Name]\nHuman Resources Expert."
}`}
              </SyntaxHighlighter>

              <h5>2. HR Policy Design</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_policy_design`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      title <code>required</code>
                    </td>
                    <td>"Human Resource Policy"</td>
                    <td>The title of the policy update document.</td>
                  </tr>
                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the document should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Title: HR Policy Design\n\nPurpose:\nThe purpose of this HR Policy Design is to provide guidelines and standards for creating effective and compliant human resource policies within the organization.\n\nPolicy Statement:\n1. All HR policies must be in line with federal, state, and local laws and regulations to ensure legal compliance.\n2. HR policies should be clearly communicated to all employees through an accessible and transparent platform.\n3. HR policies should be reviewed regularly to reflect changes in laws, regulations, and organizational needs.\n4. HR policies should be fair, consistent, and applied equally to all employees across the organization.\n5. HR policies should support the organization's values, goals, and mission while promoting a positive work culture.\n6. HR policies should be easy to understand and follow to ensure clarity and consistency in implementation.\n7. HR policies should promote diversity, inclusion, and a safe work environment for all employees.\n8. Any deviations from HR policies must be documented and approved by HR leadership to maintain accountability.\n\nImplementation:\n1. HR leadership will be responsible for overseeing the development, implementation, and monitoring of HR policies.\n2. HR managers will be responsible for communicating and enforcing HR policies within their respective departments.\n3. Employees are expected to familiarize themselves with and adhere to HR policies to maintain a productive and compliant work environment.\n\nEnforcement:\nViolation of HR policies may result in disciplinary action, up to and including termination of employment.\n\nThis HR Policy Design is effective immediately upon approval and will be reviewed annually for updates and improvements."
}`}
              </SyntaxHighlighter>

              <h5>3. Recruitment Strategy</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_recruitment_strategy`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>

              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      talent <code>required</code>
                    </td>
                    <td>"Software Engineer"</td>
                    <td>The specific role or talent being evaluated or recruited.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the document or output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "To attract top software engineering talent, we will need to implement a multifaceted recruitment strategy that will set us apart from our competitors. This strategy will focus on building a strong employer brand, leveraging various recruitment channels, and creating an engaging candidate experience.\n\nFirst, we will need to showcase our company culture and values through our employer branding efforts. This can include highlighting employee testimonials, showcasing our work environment, and emphasizing our commitment to innovation and professional development.\n\nNext, we will leverage multiple recruitment channels to reach potential candidates. This can include online job boards, social media platforms, tech-specific forums, and industry networking events. By casting a wide net, we can increase our chances of attracting the best talent in the software engineering field.\n\nLastly, we will ensure that our recruitment process is streamlined and engaging for candidates. This can include providing prompt feedback, offering a transparent view of our company culture and growth opportunities, and providing a positive candidate experience from initial application to final offer.\n\nBy implementing these strategies, we can position ourselves as a top employer for software engineers and attract the best talent in the industry to join our team."
}`}
              </SyntaxHighlighter>

              <h5>4. CV Screening</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_cv_screening`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      pdf_resume <code>required</code>
                    </td>
                    <td>"https://example.com/Junior_software_developer.pdf"</td>
                    <td>URL to the candidate's PDF resume.</td>
                  </tr>
                  <tr>
                    <td>
                      job_description <code>required</code>
                    </td>
                    <td>"Software Developer"</td>
                    <td>The job title or role for which the candidate is applying.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the document or output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Candidate's Resume Summary: \nExample User is a junior software developer with a BA (Hons) in Computer Science and experience as an Accounts Assistant at a firm where she worked on system maintenance, testing, support, and development. She has skills in SQL, C++, and .NET among others, and is seeking a software developer role to further her career.\n\nDetailed Comparison:\n- Skills: Salma has experience in SQL which is a crucial skill for a software developer. She also has exposure to C++ and .NET, aligning with the job requirements.\n- Experiences: While her role as an Accounts Assistant involved software support and testing, it lacks direct experience in coding applications and mobile applications development.\n- Qualifications: Salma holds a BA in Computer Science, which is relevant to the software developer position.\n- Unique Aspects: Salma's ability to communicate complex issues and manage projects independently could be valuable contributions to the team.\n\nCompatibility Rating: 7/10\nSalma's strong skills in SQL, C++, and .NET, coupled with her academic background in Computer Science, make her a good fit for the software developer role. However, her lack of direct experience in coding applications and mobile development slightly lowers her compatibility rating.\n\nRecommendation:\nConsidering Salma's technical skills, academic qualifications, and the potential she brings in terms of communication and project management, I recommend that the employer consider her for an interview. With the right training and support, Salma has the potential to excel in the software developer role and contribute positively to the team."
}`}
              </SyntaxHighlighter>

              <h5>5. Hire Letter</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_hire_letter`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      title <code>required</code>
                    </td>
                    <td>"Software Engineer"</td>
                    <td>The job title of the individual.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Dear [Candidate],\n\nI am thrilled to extend an offer to you for the position of Software Engineer at our company. Your exceptional skills and experience make you a perfect fit for our team, and we are excited about the possibility of you joining us.\n\nAs a Software Engineer, you will have the opportunity to work on cutting-edge projects and contribute to the development of innovative solutions that will make a real impact on our business. Your expertise in [specific programming languages or technologies] will be a valuable asset to our team, and we are confident that you will thrive in our fast-paced and dynamic work environment.\n\nIn addition to the challenging and rewarding work you will be doing, you will have access to a competitive salary and benefits package, as well as opportunities for professional development and growth within the company. We value our employees and foster a supportive and inclusive work culture that promotes collaboration, creativity, and excellence.\n\nWe are excited about the prospect of you joining our team and are looking forward to the positive impact you will make on our organization. Please let us know if you have any questions or concerns, and we will be happy to address them.\n\nSincerely,\n\n[Your Name]  \nHuman Resources Manager  \n[Company Name]"
}`}
              </SyntaxHighlighter>

              <h5>6. Termination Letter</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_termination_letter`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      title <code>required</code>
                    </td>
                    <td>"Software Engineer"</td>
                    <td>The job title of the individual.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Dear [Employee's Name],\n\nAfter careful consideration and evaluation of your performance, it is with great regret that we must inform you of our decision to terminate your employment as a Software Engineer at [Company Name], effective immediately.\n\nYour work on various projects and tasks has not consistently met the expectations and requirements of the role, despite efforts to provide additional training and support. We have closely monitored your progress and have determined that your performance does not align with the standards set by our organization.\n\nThe decision to terminate your employment was not made lightly, but rather after thorough review and consideration of all relevant factors. We believe this is the best course of action for both the company and yourself.\n\nPlease be aware that this decision is final and there will be no opportunity for reinstatement. We will provide you with all necessary information regarding your final paycheck, benefits, and any other relevant matters in a separate communication.\n\nWe appreciate the contributions you have made during your time with us and wish you the best in your future endeavors.\n\nSincerely,\n\n[Your Name]\n[Title]\n[Company Name]"
}`}
              </SyntaxHighlighter>

              <h5>7. Job Description</h5>

              <br />
              <h6>Endpoint</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      title <code>required</code>
                    </td>
                    <td>"Software Engineer"</td>
                    <td>The job title of the individual.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Job Title: Software Engineer\n\nCompany Overview:\nWe are a cutting-edge technology company dedicated to creating innovative solutions for the digital age. Our team of passionate individuals is driven to push the boundaries of what is possible in the world of software development. As a Software Engineer at our company, you will have the opportunity to work on exciting projects that will shape the future of our industry.\n\nPosition Overview:\nWe are seeking a talented and experienced Software Engineer to join our dynamic team. The ideal candidate will have a strong passion for technology and a knack for problem-solving. You will be responsible for designing, developing, and implementing software solutions that meet the needs of our clients and customers. This is a great opportunity for individuals who thrive in a fast-paced, collaborative environment and are eager to make a positive impact on the world through technology.\n\nKey Responsibilities:\n- Collaborate with cross-functional teams to design and develop software solutions\n- Write clean, efficient, and maintainable code using best practices and modern technologies\n- Conduct thorough testing and debugging to ensure the quality of the software\n- Update and maintain existing software applications to meet changing business needs\n- Provide technical support and guidance to team members as needed\n- Stay current on emerging technologies and trends in software development\n\nQualifications:\n- Bachelor's degree in Computer Science, Engineering, or related field\n- Proven experience as a Software Engineer or similar role\n- Strong proficiency in programming languages such as Java, C++, or Python\n- Experience with software development tools and frameworks\n- Solid understanding of software development lifecycle and methodologies\n- Ability to work independently and collaboratively in a team environment\n- Excellent problem-solving and communication skills\n\nBenefits:\n- Competitive salary and benefits package\n- Opportunities for professional growth and advancement\n- Vibrant and inclusive work culture that promotes collaboration and creativity\n- Flexible work schedule and remote work options\n- Access to cutting-edge technologies and resources to support your development\n\nJoin our team and be a part of something truly extraordinary. Apply now to become a Software Engineer and help us shape the future of technology. We look forward to welcoming you to our team!"
}`}
              </SyntaxHighlighter>

              <h5>8. Hiring Research</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_hiring_research`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      main_point <code>required</code>
                    </td>
                    <td>"Software Development"</td>
                    <td>The primary focus or topic of discussion.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Sure, I am interested in joining your company for software development. I have experience in languages such as Java, Python, and JavaScript, as well as familiarity with various frameworks and tools. I am confident in my ability to contribute effectively to your team and help drive successful software projects."
}`}
              </SyntaxHighlighter>

              <h5>9. LinkedIn Screening</h5>

              <br />
              <h6>Endpoint</h6>
              <SyntaxHighlighter language='json'>
                {`POST ${API_URL}/v1/workflow/generate_linkedin_screening`}
              </SyntaxHighlighter>

              <h6>Parameters</h6>
              <table className='table table-bordered'>
                <thead className='thead-light'>
                  <tr>
                    <th style={{width: '30%'}}>Parameter</th>
                    <th>Example Value</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      linkedin_url <code>required</code>
                    </td>
                    <td>"https://linkedin.com/in/example_user"</td>
                    <td>URL to the candidate's LinkedIn profile.</td>
                  </tr>
                  <tr>
                    <td>
                      job_description <code>required</code>
                    </td>
                    <td>"Software Engineering"</td>
                    <td>The job title or role for which the candidate is being considered.</td>
                  </tr>

                  <tr>
                    <td>output_language</td>
                    <td>"English"</td>
                    <td>The language in which the document or output should be generated.</td>
                  </tr>
                </tbody>
              </table>

              <h6>Response</h6>
              <SyntaxHighlighter language='json'>
                {`{
"success": true,
"data": "Candidate's Resume Summary:\n Example_user Islam is a Full Stack Developer with expertise in MERN stack development. He has experience working at Example Ltd. as a Software Engineer and has also been a Full Stack Developer at Self Employed. Example_user holds a Bachelor of Science degree in Computer Science from Port City International University Bangladesh.\n\nDetailed Comparison:\n- Skills: The candidate's resume mentions proficiency in MongoDB, ExpressJS, ReactJS, and NodeJS, which are relevant skills for a Software Engineering role.\n- Experiences: Example_user has experience working as a Software Engineer and a Full Stack Developer, which align well with the Software Engineering job description.\n- Qualifications: The candidate holds a Bachelor of Science degree in Computer Science, which meets the educational requirement for a Software Engineering position.\n- Unique Aspects: Example_user's experience in UI/UX design, problem-solving skills, and emphasis on collaboration and teamwork are additional strengths he brings to the role.\n\nCompatibility Rating: 9/10\nThe candidate demonstrates a strong alignment with the job description in terms of skills, experiences, and qualifications. Example_user's background in MERN stack development, experience in software engineering roles, and focus on teamwork make him a highly compatible candidate for the position.\n\nRecommendation:\nI highly recommend considering Example_user Islam for an interview for the Software Engineering position. His solid technical skills, relevant experiences, and positive attitude towards collaboration make him a strong contender for the role. Furthermore, his background in UI/UX design and problem-solving abilities could bring additional value to the development team."
}`}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      </>
    </>
  )
}

export default ApiDocs
